import React from 'react';
import {
  Button,
  Chip,
  FormControl,
  IconButton,
  Input,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

import { DropdownSelector } from './DropdownSelector';
import MultivalueInput from './MultivalueInput';
import PublisherMultivalueAutocompleteSearch from './PublisherMultivalueAutocompleteSearch';
import {
  getPossibleRuleKeys,
  getRuleDefinition,
  getRuleValue,
  isObjectEmpty,
  OPERATORS,
  removeKeyFromObject,
  shouldDisplayField,
  shouldDisplayOperator,
} from './accessRightRuleConfig';
import { getOrderedBranchKeys } from '../utils/accessRulesAdapter';

const getRuleLabel = (source, ruleKey, classes) => {
  const { ruleDefinition, operator } = getRuleDefinition(source, ruleKey);
  return (
    <>
      <strong className={classes.ruleName}>
        {ruleDefinition?.label || ruleKey}
      </strong>
      {shouldDisplayOperator(ruleDefinition) && (
        <span>
          {OPERATORS[operator]?.displayValue || 'je'}
        </span>
      )}
    </>
  );
};

export const AccessRightBranchEditor = ({
  branch,
  source,
  onChange,
  onRemove,
  classes,
}) => {
  const currentRule = branch || {};
  const availableRuleKeys = getPossibleRuleKeys(currentRule, source);

  const updateRule = (newValue) => onChange(newValue);

  const handleFieldChange = (ruleKey, value) => {
    updateRule({
      ...currentRule,
      [ruleKey]: value,
    });
  };

  const getUpdateField = (ruleKey) => {
    const { ruleDefinition } = getRuleDefinition(source, ruleKey);
    const fieldType = ruleDefinition?.field;

    if (fieldType === 'text') {
      return (
        <TextField
          value={currentRule[ruleKey][0] || ''}
          name={`${ruleKey}-value`}
          onChange={(e) => handleFieldChange(ruleKey, [e.target.value])}
        />
      );
    }

    if (fieldType === 'multitext') {
      return (
        <MultivalueInput
          className={classes.fieldFullSize}
          classes={{
            inputRoot: classes.multitextInput,
          }}
          name={`${ruleKey}-value`}
          value={currentRule[ruleKey] || []}
          onChange={(values) => handleFieldChange(ruleKey, values)}
        />
      );
    }

    if (fieldType === 'publisher-autocomplete') {
      return (
        <PublisherMultivalueAutocompleteSearch
          baseUrl="/publisher"
          searchBy="name"
          getOptionLabel={(option) => `${option.name} - ${option.id}`}
          value={currentRule[ruleKey] || []}
          onValueChange={(values) => handleFieldChange(ruleKey, values)}
          onError={console.error}
        />
      );
    }

    if (fieldType === 'brand-autocomplete') {
      return (
        <PublisherMultivalueAutocompleteSearch
          baseUrl="/publisher"
          searchBy="name"
          searchQuery={{ is_publishing_house: 0 }}
          getOptionLabel={(option) => `${option.name} - ${option.id}`}
          value={currentRule[ruleKey] || []}
          onValueChange={(values) => handleFieldChange(ruleKey, values)}
          onError={console.error}
        />
      );
    }

    if (fieldType === 'select') {
      return (
        <FormControl className={classes.selectSmall}>
          <Select
            name={`${ruleKey}-value`}
            value={currentRule[ruleKey][0] || ''}
            onChange={(e) => handleFieldChange(ruleKey, [e.target.value])}
          >
            {Object.keys(ruleDefinition.options).map((key) => (
              <MenuItem key={key} value={key}>{ruleDefinition.options[key]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (fieldType === 'multiselect') {
      return (
        <FormControl className={classes.fieldFullSize}>
          <Select
            name={`${ruleKey}-value`}
            multiple
            value={currentRule[ruleKey] || []}
            onChange={(e) => handleFieldChange(ruleKey, e.target.value)}
            input={<Input />}
            renderValue={(selected) => (
              <div>
                {selected.map((value) => (
                  <Chip key={value} label={ruleDefinition.options[value]} className={classes.multiselectChip} />
                ))}
              </div>
            )}
          >
            {Object.keys(ruleDefinition.options).map((key) => (
              <MenuItem key={key} value={key}>{ruleDefinition.options[key]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return '';
  };

  return (
    <div className={classes.branch}>
      {!isObjectEmpty(currentRule) && (
        <ul className={classes.ruleList}>
          {getOrderedBranchKeys(currentRule).map((ruleKey) => (
            <li className={classes.ruleLine} key={ruleKey}>
              {getRuleLabel(source, ruleKey, classes)}
              {getUpdateField(ruleKey)}
              <IconButton
                className={classes.ruleLineButton}
                size="small"
                onClick={() => updateRule(removeKeyFromObject(currentRule, ruleKey))}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      <div class={classes.ruleLine}>
      {availableRuleKeys.length > 0 && (
        <div className={classes.newRuleButton}>
          <DropdownSelector
            title="Nové pravidlo"
            onOptionSelect={(ruleKey) => handleFieldChange(
              ruleKey,
              getRuleDefinition(source, ruleKey).ruleDefinition?.value || [],
            )}
            availableOptions={availableRuleKeys}
          />
        </div>
      )}
      {onRemove && (
          <Button className={classes.bottomActions}
                  color="secondary" type="button"
                  onClick={onRemove}>
            Odstranit pravidlo
          </Button>
      )}
      </div>
    </div>
  );
};

export const AccessRightBranchList = ({ branches, source, classes }) => {
  if (!branches || branches.length === 0) {
    return (
      <em>
        Bez omezení
      </em>
    );
  }

  return branches.map((branch, branchIndex) => (
    <ul className={classes.ruleList} key={`branch-${branchIndex}`}>
      {getOrderedBranchKeys(branch).map((ruleKey) => {
        const { ruleDefinition, operator } = getRuleDefinition(source, ruleKey);
        return (
          <li className={classes.ruleLine} key={`${branchIndex}-${ruleKey}`}>
            {getRuleLabel(source, ruleKey, classes)}
            {shouldDisplayField(ruleDefinition) && (
              <span>
                {getRuleValue(
                  branch[ruleKey],
                  ruleDefinition?.options,
                  ruleDefinition ? OPERATORS[operator]?.joinWord : undefined,
                )}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  ));
};
